import * as Cesium from "cesium";
import { getUsernameAndUserIdFormat } from "../Utility";

/**
 * Parse IDs from textarea content. Supports comma and newline separators.
 * @param {string} value
 * @returns {string[]}
 */
export function parseIdsFromText(value) {
  return value
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Collect entities array from an array of Cesium dataSources or arrays of entities.
 * @param {Array} sources
 * @returns {Array} entities
 */
function collectEntitiesFromSources(sources) {
  const all = [];
  if (!Array.isArray(sources)) return all;

  for (const src of sources) {
    if (!src) continue;

    if (Array.isArray(src)) {
      all.push(...src);
      continue;
    }

    const entities = src.entities && Array.isArray(src.entities.values) ? src.entities.values : null;

    if (entities) {
      all.push(...entities);
    }
  }

  return all;
}

/**
 * Read a property by name from Cesium entity.properties and return its raw value.
 * @param {any} entity
 * @param {string} key
 * @returns {any}
 */
export function getPropertyValue(entity, key) {
  if (!entity || !entity.properties) return undefined;

  const props = entity.properties;
  let prop = props[key];

  if (prop === undefined && props._properties && props._properties[key]) {
    prop = props._properties[key];
  }

  if (!prop) return undefined;

  if (typeof prop.getValue === "function") {
    try {
      return prop.getValue();
    } catch (e) {
      if ("_value" in prop) return prop._value;
      return undefined;
    }
  }

  if (typeof prop === "object" && "_value" in prop) {
    return prop._value;
  }

  return prop;
}

/**
 * Normalize and compare ID.
 * @param {any} raw
 * @param {string} id
 * @param {number|null} numericId
 * @returns {boolean}
 */
function matchIdValue(raw, id, numericId) {
  if (raw === undefined || raw === null) return false;

  if (numericId !== null && raw === numericId) return true;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === id) return true;
    const asNum = Number(trimmed);
    if (numericId !== null && Number.isFinite(asNum) && asNum === numericId) {
      return true;
    }
    return false;
  }

  if (typeof raw === "number") {
    if (numericId !== null && raw === numericId) return true;
    const asString = String(raw);
    return asString === id || asString.trim() === id;
  }

  const asString = String(raw);
  return asString === id || asString.trim() === id;
}

/**
 * Find main, left and right entities for a given id.
 */
export function findLaneletEntitiesForId(id, mainDataSources, sideDataSources) {
  const messages = [];

  const mainEntities = collectEntitiesFromSources(mainDataSources);
  const sideEntities = collectEntitiesFromSources(sideDataSources);

  const numericId = Number.isFinite(Number(id)) ? Number(id) : null;

  const mainEntity =
    mainEntities.find((e) => {
      const v = getPropertyValue(e, "id");
      return matchIdValue(v, id, numericId);
    }) || null;

  if (!mainEntity) {
    messages.push("Main entity for id " + id + " not found");
    return {
      id,
      mainEntity: null,
      leftEntity: null,
      rightEntity: null,
      messages,
    };
  }

  const leftLineIdRaw = getPropertyValue(mainEntity, "left_line_id");
  const rightLineIdRaw = getPropertyValue(mainEntity, "right_line_id");

  let leftEntity = null;
  let rightEntity = null;

  if (leftLineIdRaw !== undefined && leftLineIdRaw !== null) {
    const leftIdStr = String(leftLineIdRaw);
    const leftNumeric = Number.isFinite(Number(leftLineIdRaw)) ? Number(leftLineIdRaw) : null;

    leftEntity =
      sideEntities.find((e) => {
        const v = getPropertyValue(e, "id");
        return matchIdValue(v, leftIdStr, leftNumeric);
      }) || null;

    if (!leftEntity) {
      messages.push("Left entity for lanelet id " + id + " (left_line_id=" + leftLineIdRaw + ") not found");
    }
  } else {
    messages.push("left_line_id for lanelet id " + id + " is empty");
  }

  if (rightLineIdRaw !== undefined && rightLineIdRaw !== null) {
    const rightIdStr = String(rightLineIdRaw);
    const rightNumeric = Number.isFinite(Number(rightLineIdRaw)) ? Number(rightLineIdRaw) : null;

    rightEntity =
      sideEntities.find((e) => {
        const v = getPropertyValue(e, "id");
        return matchIdValue(v, rightIdStr, rightNumeric);
      }) || null;

    if (!rightEntity) {
      messages.push("Right entity for lanelet id " + id + " (right_line_id=" + rightLineIdRaw + ") not found");
    }
  } else {
    messages.push("right_line_id for lanelet id " + id + " is empty");
  }

  return {
    id,
    mainEntity,
    leftEntity,
    rightEntity,
    messages,
  };
}

function setEditorField(entity, key, userData) {
  if (!entity) return;

  const value = getUsernameAndUserIdFormat(userData);
  if (!value) return;

  const currentProps = entity.properties && typeof entity.properties.getValue === "function" ? entity.properties.getValue(Cesium.JulianDate.now()) || {} : entity.properties || {};

  entity.properties = {
    ...currentProps,
    [key]: value,
  };
}

export function switchLeftRightLines(result, userData) {
  const mainEntity = result.mainEntity;
  if (!mainEntity || !mainEntity.properties) return;

  const props = mainEntity.properties;

  const leftIdProp = props.left_line_id;
  const rightIdProp = props.right_line_id;

  const leftTypeProp = props.left_line_type;
  const rightTypeProp = props.right_line_type;

  const getVal = (p) => {
    if (!p) return null;
    if (typeof p.getValue === "function") return p.getValue();
    if (typeof p === "object" && "_value" in p) return p._value;
    return p;
  };

  const setVal = (p, v) => {
    if (!p) return;
    if (typeof p.setValue === "function") {
      p.setValue(v);
    } else if (typeof p === "object" && "_value" in p) {
      p._value = v;
    }
  };

  const leftIdVal = getVal(leftIdProp);
  const rightIdVal = getVal(rightIdProp);
  setVal(leftIdProp, rightIdVal);
  setVal(rightIdProp, leftIdVal);

  const leftTypeVal = getVal(leftTypeProp);
  const rightTypeVal = getVal(rightTypeProp);
  setVal(leftTypeProp, rightTypeVal);
  setVal(rightTypeProp, leftTypeVal);

  setEditorField(mainEntity, "att_editor", userData);

  const tmp = result.leftEntity;
  result.leftEntity = result.rightEntity;
  result.rightEntity = tmp;
}

export function reverseLaneletGeometries(result, userData) {
  const entities = [result.mainEntity, result.leftEntity, result.rightEntity].filter(Boolean);

  entities.forEach((entity) => {
    if (!entity) return;

    // 1) Polyline
    if (entity.polyline && entity.polyline.positions) {
      reversePositionsProperty(entity.polyline.positions);
    }

    // 2) Polygon
    if (entity.polygon && entity.polygon.hierarchy) {
      reverseHierarchyProperty(entity.polygon.hierarchy);
    }

    setEditorField(entity, "modifier", userData);
  });
}

/**
 * Reverse an array of Cartesian3 stored in a Cesium Property (positions).
 * @param {Cesium.Property|any} property
 */
function reversePositionsProperty(property) {
  if (!property) return;

  let value = null;

  if (typeof property.getValue === "function") {
    value = property.getValue(Cesium.JulianDate.now());
  } else if ("_value" in property) {
    value = property._value;
  }

  if (!Array.isArray(value) || value.length === 0) return;

  const reversed = value.slice().reverse();

  if (typeof property.setValue === "function") {
    property.setValue(reversed);
  } else if ("_value" in property) {
    property._value = reversed;
  }
}

/**
 * Reverse positions inside a PolygonHierarchy property.
 * @param {Cesium.Property|any} property
 */
function reverseHierarchyProperty(property) {
  if (!property) return;

  let value = null;

  if (typeof property.getValue === "function") {
    value = property.getValue(Cesium.JulianDate.now());
  } else if ("_value" in property) {
    value = property._value;
  }

  if (!value || !Array.isArray(value.positions) || value.positions.length === 0) return;

  const reversedPositions = value.positions.slice().reverse();
  const newHierarchy = new Cesium.PolygonHierarchy(reversedPositions, value.holes);

  if (typeof property.setValue === "function") {
    property.setValue(newHierarchy);
  } else if ("_value" in property) {
    property._value = newHierarchy;
  }
}

/**
 * Get Cesium color by name.
 * @param {string} name
 * @returns {Cesium.Color}
 */
function getCesiumColor(name) {
  const lower = (name || "").toLowerCase();
  if (lower === "red") return Cesium.Color.RED;
  if (lower === "blue") return Cesium.Color.BLUE;
  if (lower === "purple") return Cesium.Color.PURPLE;
  return Cesium.Color.YELLOW;
}

/**
 * Apply highlight color to an entity and keep backup for reset.
 * @param {any} entity
 * @param {string} colorName
 */
function applyEntityHighlight(entity, colorName) {
  if (!entity) return;

  const color = getCesiumColor(colorName);

  if (!entity._ciLaneletCheckerBackup) {
    const backup = {};
    if (entity.polyline) {
      backup.polylineMaterial = entity.polyline.material;
    }
    if (entity.polygon) {
      backup.polygonMaterial = entity.polygon.material;
    }
    entity._ciLaneletCheckerBackup = backup;
  }

  if (entity.polyline) {
    entity.polyline.material = new Cesium.ColorMaterialProperty(color);
  } else if (entity.polygon) {
    entity.polygon.material = new Cesium.ColorMaterialProperty(color);
  }
}

/**
 * Restore highlight state for an entity.
 * @param {any} entity
 */
function restoreEntityHighlight(entity) {
  if (!entity || !entity._ciLaneletCheckerBackup) return;

  const backup = entity._ciLaneletCheckerBackup;

  if (entity.polyline && backup.polylineMaterial !== undefined) {
    entity.polyline.material = backup.polylineMaterial;
  }
  if (entity.polygon && backup.polygonMaterial !== undefined) {
    entity.polygon.material = backup.polygonMaterial;
  }

  delete entity._ciLaneletCheckerBackup;
}

function zoomToResult(result, viewerRef) {
  if (!viewerRef || !viewerRef.current) return;

  const viewer = viewerRef.current.viewer || viewerRef.current;
  if (!viewer || !viewer.scene || !viewer.camera) return;

  const scene = viewer.scene;
  const camera = viewer.camera;
  const globe = scene.globe;
  const ellipsoid = globe && globe.ellipsoid ? globe.ellipsoid : Cesium.Ellipsoid.WGS84;

  const entities = [result.mainEntity, result.leftEntity, result.rightEntity].filter(Boolean);
  if (!entities.length) return;

  const canvas = scene.canvas;
  const centerScreen = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
  const centerRay = camera.getPickRay(centerScreen);
  const oldCenter = globe.pick(centerRay, scene);

  if (!oldCenter) {
    if (typeof viewer.flyTo === "function") {
      viewer.flyTo(entities, { duration: 0.5 });
    } else if (typeof viewer.zoomTo === "function") {
      viewer.zoomTo(entities);
    }
    return;
  }

  let boundingSphere;
  try {
    boundingSphere = Cesium.BoundingSphere.fromEntities(entities);
  } catch (e) {
    if (typeof viewer.flyTo === "function") {
      viewer.flyTo(entities, { duration: 0.5 });
    } else if (typeof viewer.zoomTo === "function") {
      viewer.zoomTo(entities);
    }
    return;
  }

  const newCenterSurface = ellipsoid.scaleToGeodeticSurface(boundingSphere.center);
  if (!newCenterSurface) {
    if (typeof viewer.flyTo === "function") {
      viewer.flyTo(entities, { duration: 0.5 });
    } else if (typeof viewer.zoomTo === "function") {
      viewer.zoomTo(entities);
    }
    return;
  }

  const diffOld = new Cesium.Cartesian3();
  Cesium.Cartesian3.subtract(oldCenter, camera.position, diffOld);
  const distanceToSurface = Cesium.Cartesian3.magnitude(diffOld);
  if (!isFinite(distanceToSurface) || distanceToSurface <= 0.0) {
    if (typeof viewer.flyTo === "function") {
      viewer.flyTo(entities, { duration: 0.5 });
    } else if (typeof viewer.zoomTo === "function") {
      viewer.zoomTo(entities);
    }
    return;
  }

  const dirToNewCenter = new Cesium.Cartesian3();
  Cesium.Cartesian3.subtract(newCenterSurface, camera.position, dirToNewCenter);
  Cesium.Cartesian3.normalize(dirToNewCenter, dirToNewCenter);

  const offsetVec = new Cesium.Cartesian3();
  Cesium.Cartesian3.multiplyByScalar(dirToNewCenter, distanceToSurface, offsetVec);
  const newCameraPosition = new Cesium.Cartesian3();
  Cesium.Cartesian3.subtract(newCenterSurface, offsetVec, newCameraPosition);

  camera.flyTo({
    destination: newCameraPosition,
    duration: 0.5,
    orientation: {
      heading: camera.heading,
      pitch: camera.pitch,
      roll: camera.roll,
    },
  });
}

/**
 * Highlight entities for a search result and zoom to them.
 * highlightToolsRef is kept for compatibility but not used.
 */
export function highlightSearchResult(result, highlightToolsRef, viewerRef) {
  if (!result) return;

  applyEntityHighlight(result.mainEntity, "red");
  applyEntityHighlight(result.rightEntity, "blue");
  applyEntityHighlight(result.leftEntity, "purple");

  zoomToResult(result, viewerRef);
}

/**
 * Clear highlight for entities in a search result.
 * highlightToolsRef is kept for compatibility but not used.
 */
export function clearHighlightForResult(result, highlightToolsRef) {
  if (!result) return;

  restoreEntityHighlight(result.mainEntity);
  restoreEntityHighlight(result.rightEntity);
  restoreEntityHighlight(result.leftEntity);
}
